/**
 * PWA Install Prompt Banner
 *
 * An unobtrusive banner that prompts users to install the app.
 * Shows platform-specific instructions for iOS.
 */

import { useState } from 'react';
import { Download, X, Share, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { cn } from '@/lib/utils';

export function InstallPromptBanner() {
  const { shouldShowPrompt, dismissPrompt, install, platform, isInstalled } =
    useInstallPrompt();
  const [isInstalling, setIsInstalling] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  if (!shouldShowPrompt || isInstalled) {
    return null;
  }

  const handleInstall = async () => {
    if (platform === 'ios') {
      setShowIOSInstructions(true);
      return;
    }

    setIsInstalling(true);
    const success = await install();
    setIsInstalling(false);

    if (success) {
      dismissPrompt(true);
    }
  };

  const handleDismiss = () => {
    dismissPrompt(false); // Temporary dismiss (14 days)
  };

  const handleDismissPermanently = () => {
    dismissPrompt(true); // Permanent dismiss
  };

  // iOS-specific instructions
  if (showIOSInstructions) {
    return (
      <div
        className={cn(
          'fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50',
          'animate-in slide-in-from-bottom-4 fade-in duration-300'
        )}
      >
        <div className="rounded-lg border bg-card shadow-lg overflow-hidden">
          <div className="p-4">
            <div className="flex items-start gap-3">
              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm mb-2">Install on iPhone/iPad</h3>
                <ol className="text-xs text-muted-foreground space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">1</span>
                    <span>Tap the <Share className="inline h-3.5 w-3.5 mx-0.5" /> Share button</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">2</span>
                    <span>Scroll and tap <Plus className="inline h-3.5 w-3.5 mx-0.5" /> Add to Home Screen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">3</span>
                    <span>Tap "Add" to install</span>
                  </li>
                </ol>

                <div className="flex items-center gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowIOSInstructions(false)}
                    className="h-8"
                  >
                    Back
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleDismiss}
                    className="h-8 text-muted-foreground"
                  >
                    Maybe later
                  </Button>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={handleDismissPermanently}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 -mt-1 -mr-1"
                title="Don't show again"
                aria-label="Dismiss permanently"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50',
        'animate-in slide-in-from-bottom-4 fade-in duration-300'
      )}
    >
      <div className="rounded-lg border bg-card shadow-lg overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Download className="h-5 w-5 text-primary" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm">Install Badminton Leagues</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Add to your home screen for quick access and offline support.
                {platform === 'ios' && ' Works best as an app on iPhone!'}
              </p>

              {/* Buttons */}
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={handleInstall}
                  disabled={isInstalling}
                  className="h-8"
                >
                  {isInstalling ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Installing...
                    </>
                  ) : (
                    'Install'
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDismiss}
                  className="h-8 text-muted-foreground"
                >
                  Not now
                </Button>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={handleDismissPermanently}
              className="text-muted-foreground hover:text-foreground transition-colors p-1 -mt-1 -mr-1"
              title="Don't show again"
              aria-label="Dismiss permanently"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

